import { db, auth } from '@/firebase';
import {
  doc,
  collection,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';

/**
 * Sanitizar profileKey para usarlo en docId de Firestore
 * Convierte a lowercase, reemplaza espacios por _, quita caracteres especiales
 * @param {string} profileKey - Nombre del perfil
 * @returns {string} - Perfil sanitizado
 */
function sanitizeProfileKey(profileKey) {
  if (!profileKey) return '';
  return profileKey
    .toLowerCase()
    .replace(/\s+/g, '_') // Reemplazar espacios por _
    .replace(/[^a-z0-9_-]/g, ''); // Quitar caracteres que no sean alphanuméricas, _, -
}

/**
 * Guardar un nuevo aviso en Firestore
 * @param {string} workspaceId - ID del workspace
 * @param {string} message - Mensaje del aviso
 * @param {string} createdByProfile - Nombre del perfil que crea el aviso (opcional)
 * @param {string} createdByProfileId - ID único del perfil (para filtrar emisor)
 * @returns {Promise<string>} - ID del aviso creado
 */
export async function saveAlert(workspaceId, message, createdByProfile, createdByProfileId) {
  const uid = auth.currentUser?.uid;
  const displayName = auth.currentUser?.displayName || 'Usuario';

  if (!uid || !message || message.trim() === '') {
    throw new Error('Datos inválidos para guardar aviso');
  }

  // Generar alertId único
  const alertId = `alert_${Date.now()}`;

  const alertRef = doc(db, `workspaces/${workspaceId}/settings`, 'alerts');

   try {
     await setDoc(
       alertRef,
       {
         alertId,
         message: message.trim(),
         createdAt: serverTimestamp(),
         createdBy: uid,
         createdByName: displayName,
         createdByProfile: createdByProfile || 'Equipo',
         createdByProfileId: createdByProfileId || createdByProfile || 'unknown',
         active: true,
       },
       { merge: true }
     );

    console.log('[ALERTS] Alert saved:', alertId);
    return alertId;
  } catch (error) {
    console.error('[ALERTS] Error saving alert:', error);
    throw error;
  }
}

/**
 * Suscribirse al aviso global del workspace
 * @param {string} workspaceId - ID del workspace
 * @param {Function} callback - Función llamada cuando hay cambios
 * @returns {Function} - Función para desuscribirse
 */
export function subscribeToAlert(workspaceId, callback) {
  if (!workspaceId) {
    console.warn('[ALERTS] No workspaceId provided');
    return () => {};
  }

  const alertRef = doc(db, `workspaces/${workspaceId}/settings`, 'alerts');

  const unsub = onSnapshot(alertRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.data();
      console.log('[ALERTS] Alert updated:', data);
      callback(data);
    } else {
      console.log('[ALERTS] No alert found');
      callback(null);
    }
  });

  return unsub;
}

/**
 * Suscribirse a los avisos descartados por el perfil
 * @param {string} workspaceId - ID del workspace
 * @param {string} profileKey - Nombre del perfil (se sanitiza para docId)
 * @param {Function} callback - Función llamada cuando hay cambios
 * @returns {Function} - Función para desuscribirse
 */
export function subscribeToDismissed(workspaceId, profileKey, callback) {
  if (!workspaceId || !profileKey) {
    console.warn('[ALERTS] No workspaceId or profileKey provided');
    return () => {};
  }

  const sanitizedProfileKey = sanitizeProfileKey(profileKey);
  const dismissedRef = doc(
    db,
    "workspaces",
    workspaceId,
    "settings",
    `alertsDismissed_profile_${sanitizedProfileKey}`
  );

  const unsub = onSnapshot(dismissedRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.data();
      console.log('[ALERTS] Dismissed alert updated for profile:', profileKey, data);
      callback(data);
    } else {
      console.log('[ALERTS] No dismissed alerts found for profile:', profileKey);
      callback(null);
    }
  });

  return unsub;
}

/**
 * Marcar un aviso como descartado para el perfil actual
 * @param {string} workspaceId - ID del workspace
 * @param {string} profileKey - Nombre del perfil (se sanitiza para docId)
 * @param {string} alertId - ID del aviso a descartar
 * @returns {Promise<void>}
 */
export async function markAlertAsDismissed(workspaceId, profileKey, alertId, profileId) {
  if (!workspaceId || !profileKey || !alertId) {
    throw new Error('Missing required parameters');
  }

  const sanitizedProfileKey = sanitizeProfileKey(profileKey);
  const dismissedRef = doc(
    db,
    "workspaces",
    workspaceId,
    "settings",
    `alertsDismissed_profile_${sanitizedProfileKey}`
  );

  // profileId canónico (UUID de accountMemberships/config.profiles), NUNCA
  // derivado del sufijo sanitizado del docId. Necesario para que Firestore
  // Rules pueda verificar server-side (isWorkerAssigned/isCanonicalAdminOf)
  // que quien escribe/lee este dismissed es realmente el perfil propietario,
  // sin depender de la sanitización client-side. Si no se provee (llamadas
  // legacy), se usa profileKey como fallback para no romper la firma previa.
  const canonicalProfileId = profileId || profileKey;

  try {
    await setDoc(
      dismissedRef,
      {
        lastDismissedAlertId: alertId,
        dismissedAt: serverTimestamp(),
        profileId: canonicalProfileId,
      },
      { merge: true }
    );

    console.log('[ALERTS] Alert dismissed for profile:', profileKey, alertId, 'profileId:', canonicalProfileId);
  } catch (error) {
    console.error('[ALERTS] Error dismissing alert:', error);
    throw error;
  }
}

/**
 * Sembrar el estado de "descartado" para un perfil recién creado, únicamente
 * con el alertId del aviso broadcast que YA estaba activo en el momento de
 * guardar el perfil por primera vez. No crea ningún mecanismo permanente:
 * si más adelante se publica un aviso nuevo con otro alertId, no coincidirá
 * con el lastDismissedAlertId sembrado aquí y se mostrará con normalidad.
 *
 * Debe llamarse SOLO después de que el guardado del perfil nuevo haya
 * terminado correctamente en Firestore (settings/profiles), y solo para
 * perfiles que eran nuevos antes de ese guardado. No debe usarse para
 * perfiles ya existentes ni como sustituto de markAlertAsDismissed
 * (dismiss manual) o de ACK/reply.
 *
 * Reutiliza sanitizeProfileKey y la misma estructura de documento que
 * markAlertAsDismissed (mismo path alertsDismissed_profile_<sanitized>).
 *
 * @param {string} workspaceId - ID del workspace
 * @param {string} profileKey - Nombre/clave del perfil nuevo (se sanitiza para docId)
 * @param {string} profileId - ID canónico del perfil nuevo (UUID real)
 * @returns {Promise<boolean>} - true si se sembró el dismiss, false si no había aviso activo que sembrar
 */
export async function seedDismissedForNewProfile(workspaceId, profileKey, profileId) {
  if (!workspaceId || !profileKey) {
    console.warn('[ALERTS] seedDismissedForNewProfile: falta workspaceId o profileKey');
    return false;
  }

  const alertRef = doc(db, `workspaces/${workspaceId}/settings`, 'alerts');

  let currentAlertId;
  try {
    const alertSnap = await getDoc(alertRef);
    if (!alertSnap.exists()) {
      console.log('[ALERTS] seedDismissedForNewProfile: no hay aviso activo, nada que sembrar');
      return false;
    }
    currentAlertId = alertSnap.data()?.alertId;
    if (!currentAlertId) {
      console.log('[ALERTS] seedDismissedForNewProfile: aviso sin alertId, nada que sembrar');
      return false;
    }
  } catch (error) {
    console.error('[ALERTS] seedDismissedForNewProfile: error leyendo aviso actual:', error);
    throw error;
  }

  const sanitizedProfileKey = sanitizeProfileKey(profileKey);
  const dismissedRef = doc(
    db,
    "workspaces",
    workspaceId,
    "settings",
    `alertsDismissed_profile_${sanitizedProfileKey}`
  );

  // profileId canónico, mismo criterio que markAlertAsDismissed: nunca
  // derivado del sufijo sanitizado del docId, con fallback a profileKey
  // solo si no se provee explícitamente.
  const canonicalProfileId = profileId || profileKey;

  try {
    await setDoc(
      dismissedRef,
      {
        lastDismissedAlertId: currentAlertId,
        dismissedAt: serverTimestamp(),
        profileId: canonicalProfileId,
      },
      { merge: true }
    );

    console.log('[ALERTS] Seed dismissed para perfil nuevo:', profileKey, currentAlertId, 'profileId:', canonicalProfileId);
    return true;
  } catch (error) {
    console.error('[ALERTS] seedDismissedForNewProfile: error escribiendo dismiss semilla:', error);
    throw error;
  }
}

/**
 * Enviar una reacción dirigida (OK/respuesta) al creador del aviso original.
 * Crea un documento independiente en alertsReplies (autoId), NUNCA sobrescribe

 * el documento singleton de alerts. El destinatario debe proceder siempre del
 * aviso original vigente (currentAlert.createdByProfileId / currentAlert.alertId).
 * @param {string} workspaceId - ID del workspace
 * @param {Object} params
 * @param {'ack'|'reply'} params.type - Tipo de reacción
 * @param {string} params.message - Mensaje (para ack puede ser mensaje fijo)
 * @param {string} params.senderProfileId - Perfil que reacciona
 * @param {string} params.senderProfileName - Nombre del perfil que reacciona
 * @param {string} params.recipientProfileId - createdByProfileId del aviso original
 * @param {string} params.originAlertId - alertId del aviso original
 * @returns {Promise<string>} - ID del documento de reacción creado
 */
export async function sendAlertReply(workspaceId, {
  type,
  message,
  senderProfileId,
  senderProfileName,
  recipientProfileId,
  originAlertId,
}) {
  if (!workspaceId) {
    throw new Error('workspaceId requerido para enviar reacción');
  }
  if (!recipientProfileId) {
    throw new Error('recipientProfileId requerido: no se puede enviar reacción sin destinatario');
  }
  if (!originAlertId) {
    throw new Error('originAlertId requerido: no se puede enviar reacción sin referencia al aviso original');
  }
  if (type !== 'ack' && type !== 'reply') {
    throw new Error('type inválido para reacción: debe ser "ack" o "reply"');
  }

  const repliesRef = collection(db, `workspaces/${workspaceId}/alertsReplies`);

  try {
    const docRef = await addDoc(repliesRef, {
      type,
      message: (message || '').toString(),
      senderProfileId: senderProfileId || 'unknown',
      senderProfileName: senderProfileName || 'Equipo',
      recipientProfileId,
      originAlertId,
      createdAt: serverTimestamp(),
      handled: false,
    });

    console.log('[ALERTS] Reply sent:', docRef.id, { type, recipientProfileId, originAlertId });
    return docRef.id;
  } catch (error) {
    console.error('[ALERTS] Error sending reply:', error);
    throw error;
  }
}

/**
 * Suscribirse a la reacción dirigida (reply/ack) pendiente más antigua para
 * el perfil activo. Cola FIFO: siempre entrega la más antigua no gestionada
 * (handled === false). Al marcar la actual como handled, el propio onSnapshot
 * entrega automáticamente la siguiente pendiente si existe.
 * @param {string} workspaceId - ID del workspace
 * @param {string} myProfileId - Perfil activo (destinatario)
 * @param {Function} callback - Función llamada con la reply pendiente ({id, ...data}) o null
 * @returns {Function} - Función para desuscribirse
 */
export function subscribeToPendingReply(workspaceId, myProfileId, callback) {
  if (!workspaceId || !myProfileId) {
    console.warn('[ALERTS] No workspaceId or myProfileId provided for pending reply subscription');
    return () => {};
  }

  const repliesRef = collection(db, `workspaces/${workspaceId}/alertsReplies`);
  const pendingQuery = query(
    repliesRef,
    where('recipientProfileId', '==', myProfileId),
    where('handled', '==', false),
    orderBy('createdAt', 'asc'),
    limit(1)
  );

  const unsub = onSnapshot(pendingQuery, (snapshot) => {
    if (!snapshot.empty) {
      const docSnap = snapshot.docs[0];
      const data = { id: docSnap.id, ...docSnap.data() };
      console.log('[ALERTS] Pending reply updated for profile:', myProfileId, data);
      callback(data);
    } else {
      console.log('[ALERTS] No pending replies for profile:', myProfileId);
      callback(null);
    }
  });

  return unsub;
}

/**
 * Marcar una reacción (reply/ack) como gestionada por el destinatario.
 * Solo modifica el campo handled; el resto del documento permanece intacto.
 * @param {string} workspaceId - ID del workspace
 * @param {string} replyId - ID del documento de reacción a marcar
 * @returns {Promise<void>}
 */
export async function markReplyAsHandled(workspaceId, replyId) {
  if (!workspaceId || !replyId) {
    throw new Error('Missing required parameters');
  }

  const replyRef = doc(db, `workspaces/${workspaceId}/alertsReplies`, replyId);

  try {
    await updateDoc(replyRef, { handled: true });
    console.log('[ALERTS] Reply marked as handled:', replyId);
  } catch (error) {
    console.error('[ALERTS] Error marking reply as handled:', error);
    throw error;
  }
}


